import { useCallback, useEffect, useMemo, useState } from "react";
import {
  WEEKDAYS,
  blockTypeMeta,
  deleteBlock,
  deleteSlot,
  fetchBlocks,
  fetchSlots,
  updateSlot,
  type AvailabilityBlock,
  type AvailabilitySlot,
} from "@/lib/availability";
import SlotModal from "@/pages/admin/availability/components/SlotModal";
import { parseISODate } from "@/pages/admin/format";

interface SlotGroup {
  key: string;
  scope: string;
  target: string;
  slots: AvailabilitySlot[];
}

export default function AdminAvailability() {
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [blocks, setBlocks] = useState<AvailabilityBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AvailabilitySlot | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [slotData, blockData] = await Promise.all([fetchSlots(), fetchBlocks()]);
      setSlots(slotData);
      setBlocks(blockData);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn't load availability. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo<SlotGroup[]>(() => {
    const map = new Map<string, SlotGroup>();
    slots.forEach((slot) => {
      const key = `${slot.scope}|${slot.target || "All"}`;
      if (!map.has(key)) {
        map.set(key, { key, scope: slot.scope, target: slot.target || "All", slots: [] });
      }
      map.get(key)?.slots.push(slot);
    });
    const list = Array.from(map.values());
    list.forEach((group) => group.slots.sort((a, b) => a.weekday - b.weekday));
    return list;
  }, [slots]);

  const activeSlots = slots.filter((slot) => slot.active);
  const weeklyCapacity = activeSlots.reduce((sum, slot) => sum + slot.capacity, 0);
  const targetsCovered = new Set(slots.map((slot) => slot.target || "All")).size;

  const stats = [
    { label: "Time slots", value: slots.length, icon: "ri-time-line", tone: "primary" },
    { label: "Active slots", value: activeSlots.length, icon: "ri-checkbox-circle-line", tone: "secondary" },
    { label: "Farms / experiences", value: targetsCovered, icon: "ri-plant-line", tone: "accent" },
    { label: "Weekly capacity", value: weeklyCapacity, icon: "ri-group-line", tone: "primary" },
  ];

  const toneBubble: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-900",
    secondary: "bg-secondary-100 text-secondary-700",
  };

  const handleToggle = async (slot: AvailabilitySlot) => {
    const next = !slot.active;
    setSlots((list) => list.map((item) => (item.id === slot.id ? { ...item, active: next } : item)));
    try {
      await updateSlot(slot.id, { active: next });
    } catch {
      setSlots((list) => list.map((item) => (item.id === slot.id ? { ...item, active: slot.active } : item)));
      setError("We couldn't update that slot. Please try again.");
    }
  };

  const handleDeleteSlot = async (id: number) => {
    try {
      await deleteSlot(id);
      setSlots((list) => list.filter((item) => item.id !== id));
      setConfirmId(null);
    } catch {
      setError("We couldn't delete that slot. Please try again.");
    }
  };

  const handleRemoveBlock = async (id: number) => {
    try {
      await deleteBlock(id);
      setBlocks((list) => list.filter((item) => item.id !== id));
    } catch {
      setError("We couldn't remove that block. Please try again.");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 md:mb-8">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
            Operations
          </span>
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-2">
            Availability &amp; slots
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-2xl">
            Manage recurring weekly time slots, capacity and closures for every farm and experience.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Add slot
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3.5 mb-5">
          <p className="flex items-start gap-2 text-sm text-accent-900 flex-1">
            <i className="ri-error-warning-line text-lg"></i>
            <span>{error}</span>
          </p>
          <button
            type="button"
            onClick={() => setError("")}
            className="text-sm font-semibold text-accent-900 underline whitespace-nowrap cursor-pointer self-start"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            <span className={`w-10 h-10 flex items-center justify-center rounded-full ${toneBubble[stat.tone]}`}>
              <i className={`${stat.icon} text-xl`}></i>
            </span>
            <p className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950 mt-4">
              {stat.value}
            </p>
            <p className="text-xs md:text-sm text-foreground-600 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="bg-background-50 border border-background-200 rounded-lg py-20 mt-5 flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
          <p className="text-sm text-foreground-600">Loading availability…</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mt-5">
          <div className="xl:col-span-2 space-y-5">
            {groups.length === 0 ? (
              <div className="bg-background-50 border border-background-200 rounded-lg py-16 text-center">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line text-3xl"></i>
                </span>
                <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">No slots yet</h3>
                <p className="text-foreground-600 mt-3">Add your first recurring weekly time slot to get started.</p>
              </div>
            ) : (
              groups.map((group) => (
                <section key={group.key} className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-background-200">
                    <div className="min-w-0">
                      <h2 className="font-heading text-lg text-foreground-950 font-semibold truncate">
                        {group.target}
                      </h2>
                      <span className="inline-flex items-center text-[11px] font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2 py-0.5 rounded-full mt-1 capitalize">
                        {group.scope}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">
                      {group.slots.length} {group.slots.length === 1 ? "slot" : "slots"}
                    </span>
                  </div>

                  <ul className="divide-y divide-background-200">
                    {group.slots.map((slot) => (
                      <li key={slot.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                        <span className="w-24 shrink-0 text-sm font-semibold text-foreground-950">
                          {WEEKDAYS[slot.weekday]}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-sm text-foreground-700 whitespace-nowrap">
                          <i className="ri-time-line text-primary-600"></i>
                          {slot.start_time} – {slot.end_time}
                        </span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          Capacity {slot.capacity} · {slot.min_guests}–{slot.max_guests} guests
                        </span>

                        <div className="ml-auto flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggle(slot)}
                            role="switch"
                            aria-checked={slot.active}
                            aria-label="Toggle slot active"
                            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                              slot.active ? "bg-primary-500" : "bg-background-300"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${
                                slot.active ? "left-[22px]" : "left-0.5"
                              }`}
                            ></span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(slot);
                              setModalOpen(true);
                            }}
                            aria-label="Edit slot"
                            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                          >
                            <i className="ri-pencil-line"></i>
                          </button>
                          {confirmId === slot.id ? (
                            <span className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleDeleteSlot(slot.id)}
                                className="text-xs font-semibold bg-accent-600 text-background-50 px-2.5 py-1.5 rounded-md hover:bg-accent-700 cursor-pointer whitespace-nowrap"
                              >
                                Delete
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmId(null)}
                                className="text-xs font-semibold text-foreground-600 px-2 py-1.5 rounded-md hover:bg-background-100 cursor-pointer whitespace-nowrap"
                              >
                                No
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmId(slot.id)}
                              aria-label="Delete slot"
                              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            )}
          </div>

          <div>
            <section className="bg-background-50 border border-background-200 rounded-lg p-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-heading text-lg text-foreground-950 font-semibold">Blocked dates</h2>
                <span className="text-xs text-foreground-500">{blocks.length}</span>
              </div>
              <p className="text-xs text-foreground-500 mt-1">
                Closures synced from the calendar.
              </p>
              <div className="mt-4 space-y-2">
                {blocks.length === 0 ? (
                  <p className="text-sm text-foreground-500 py-2">No blocked dates.</p>
                ) : (
                  blocks.map((block) => {
                    const meta = blockTypeMeta(block.block_type);
                    const start = parseISODate(block.start_date);
                    const end = parseISODate(block.end_date);
                    const range =
                      block.start_date === block.end_date
                        ? start.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                        : `${start.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
                    return (
                      <div key={block.id} className="rounded-md bg-background-100 border border-background-200 px-3 py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.chip}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                            {meta.label}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveBlock(block.id)}
                            aria-label="Remove block"
                            className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 hover:text-foreground-800 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                        <p className="text-sm font-semibold text-foreground-900 mt-1.5 truncate">
                          {block.target || "All farms"}
                        </p>
                        <p className="text-xs text-foreground-500 mt-0.5">{range}</p>
                        {block.reason ? (
                          <p className="text-xs text-foreground-600 mt-1">{block.reason}</p>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </div>
      )}

      <SlotModal
        open={modalOpen}
        editing={editing}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </div>
  );
}